import React, {useMemo, useState} from 'react';
import Form from "../../components/form/Form";
import {useAuth} from "../../providers/AuthProvider";
import {Select} from 'antd';
import {usePositionOverride} from "../../providers/PositionOverrideProvider";
import {usePosition} from "../../providers/ProjectPositionProvider";
import SpinLoader from "../../components/SpinLoader";
import {useAPI} from "../../config/API";
import {useProject} from "../../providers/ClientProjectProvider";
import EmptyFeedback from "../../components/EmptyFeedback";

const PER_PAGE = 20;

const PositionCandidateGreenlightOptionsModal = ({onClose}) => {
    const {auth} = useAuth();
    const {project} = useProject();
    const {position} = usePosition();
    const {linkGreenlightJobToCandidate, isDisabled} = usePositionOverride();
    const candidate = position.assigned_candidate;

    const [greenlightId, setGreenlightId] = useState(null);
    const [status, setStatus] = useState(undefined);
    // Pages are zero indexed, so page 0 is the first page.
    const [page, setPage] = useState(0);

    const {
        data: glData,
        error,
        isLoading,
        isFetching,
        refetch
    } = useAPI(['fetchGreenlightOptionsForCandidate', {
        position_id: position.id,
        project_id: project.id,
        status,
        page,
        per_page: PER_PAGE
    }], {
        // enabled: !!status,
        retry: 3,
        onSuccess: (response) => {
            console.log('onSuccess', response)
        }
    })

    const statusOptions = [
        {label: 'Pending', value: 0},
        {label: 'Active', value: 1000},
        {label: 'Complete', value: 2000},
    ];

    const glJobs = useMemo(() => {
        if (isLoading || !glData?.jobs?.length) return [];
        return glData.jobs;
    }, [glData?.jobs, isLoading]);

    const meta = glData?.meta || {};
    const currentPage = meta.current_page ?? page;
    const lastPage = meta.last_page ?? 0;
    const total = meta.total || 0;

    const statusBadge = (job) => {
        const map = {
            0:    {label: 'Pending',  cls: 'badge bg-warning text-dark'},
            1000: {label: 'Active',   cls: 'badge bg-success'},
            2000: {label: 'Complete', cls: 'badge bg-secondary'},
        };
        const s = map[job.status] || {label: job.status_label || 'Unknown', cls: 'badge bg-light text-dark'};
        return <span className={s.cls}>{s.label}</span>;
    };

    const formatDate = (iso) => {
        if (!iso) return '—';
        return new Date(iso).toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'});
    };

    return (
        <SpinLoader isLoading={isDisabled}>
            <div>

                <React.Fragment>
                    <div>
                        <p className="my-3">
                            Use the options below to match the existing Greenlight job
                        </p>
                    </div>

                    <div className="mb-3">
                        <label className="form-label font-semibold">Choose A Status <span className="italic text-wrippleDangerDark ml-2">* required</span></label>
                        <Select
                            value={status}
                            onChange={(val) => {
                                setGreenlightId(null);
                                setPage(0);
                                setStatus(val);
                            }}
                            options={[{
                                label: 'Select an option',
                                disabled: true,
                                value: undefined
                            },...statusOptions]}
                            style={{width: '100%'}}
                        />
                    </div>

                    <div className="mb-3">

                        <label className="form-label font-semibold">Greenlight Jobs</label>

                        {status === undefined ? (
                            <EmptyFeedback>
                                <div className="flex flex-col items-center">
                                    <p className="text-sm font-medium text-center pb-6">
                                        Please choose a job status
                                    </p>
                                </div>
                            </EmptyFeedback>
                        ) : (
                            <div className="border rounded flex flex-col items-stretch" style={{maxHeight: 420, overflowY: 'auto'}}>
                                {isFetching ? (
                                    <div className="p-4 text-center text-sm text-gray-500">
                                        Loading jobs&hellip;
                                    </div>
                                ) : error ? (
                                    <div className="p-4 text-center">
                                        <div className="text-danger mb-2">Failed to load jobs from Greenlight</div>
                                        <button
                                            type="button"
                                            className="btn btn-dark"
                                            onClick={() => refetch()}>
                                            Retry
                                        </button>
                                    </div>
                                ) : glJobs.length === 0 ? (
                                    <div className="p-4 text-center text-sm text-gray-500">No jobs found</div>
                                ) : (
                                    glJobs.map((job) => {
                                        const isSelected = greenlightId === job.id;
                                        const isJobDisabled = !!job.disabled;
                                        return (
                                            <div
                                                key={job.id}
                                                onClick={() => {
                                                    if (isJobDisabled) return;
                                                    setGreenlightId(job.id);
                                                }}
                                                aria-disabled={isJobDisabled}
                                                className="p-3"
                                                style={{
                                                    cursor: isJobDisabled ? 'not-allowed' : 'pointer',
                                                    borderLeft: isSelected ? '3px solid #1677ff' : '3px solid transparent',
                                                    backgroundColor: isJobDisabled
                                                        ? '#f5f5f5'
                                                        : (isSelected ? '#e6f4ff' : 'transparent'),
                                                    borderBottom: '1px solid #f0f0f0',
                                                    opacity: isJobDisabled ? 0.6 : 1,
                                                }}
                                            >
                                                <div className="d-flex justify-content-between align-items-start mb-1">
                                                    <strong className="me-2">{job.title}</strong>
                                                    {statusBadge(job)}
                                                </div>
                                                {isJobDisabled && (
                                                    <div className="text-xs text-gray-500 mb-1 italic">
                                                        <i className="fa fa-lock mr-1"/>
                                                        {job.disabled_reason || 'Already linked'}
                                                    </div>
                                                )}
                                                <div className="text-muted small mb-1">
                                                    <span className="me-3">
                                                        <i className="fa fa-user me-1"/>
                                                        {job.supplierName}
                                                    </span>
                                                    <span>
                                                        <i className="fa fa-building me-1"/>
                                                        {job.clientName}
                                                    </span>
                                                </div>
                                                <div className="text-muted small mb-1">
                                                    <i className="fa fa-calendar me-1"/>
                                                    {formatDate(job.startDate)} &rarr; {formatDate(job.endDate)}
                                                </div>
                                                <div className="d-flex gap-3 small">
                                                    {job.compensation && (
                                                        <span className="text-success-dark fw-semibold">
                                                            {job.compensation.currency?.symbol}{job.compensation.amount?.toLocaleString()} / {job.compensation.type}
                                                        </span>
                                                    )}
                                                    {job.locationType && (
                                                        <span className="text-muted text-capitalize">
                                                            <i className="fa fa-map-marker me-1"/>
                                                            {job.locationType}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        )}

                        {status !== undefined && !error && total > 0 && (
                            <div className="flex flex-row justify-between items-center gap-3 mt-2">
                                <span className="text-xs text-gray-500">
                                    Showing {(currentPage * PER_PAGE) + 1}
                                    &ndash;{Math.min((currentPage + 1) * PER_PAGE, total)} of {total}
                                </span>
                                <div className="flex flex-row items-center gap-2">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-dark inline-flex items-center disabled:opacity-60 disabled:cursor-not-allowed"
                                        disabled={currentPage <= 0 || isFetching}
                                        onClick={() => setPage((p) => Math.max(0, p - 1))}>
                                        Previous
                                    </button>
                                    <span className="text-xs text-gray-500 whitespace-nowrap">
                                        Page {currentPage + 1} of {lastPage + 1}
                                    </span>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-dark inline-flex items-center disabled:opacity-60 disabled:cursor-not-allowed"
                                        disabled={currentPage >= lastPage || isFetching}
                                        onClick={() => setPage((p) => Math.min(lastPage, p + 1))}>
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    <Form
                        background={'transparent'}
                        minimal
                        disabled={isDisabled}
                        submitButton={false}
                        model={{greenlight_id: greenlightId}}
                    >
                        <div className="flex mt-5 flex-row justify-end gap-3">

                            <button
                                type="button" disabled={isDisabled}
                                className="btn btn-outline-dark" onClick={onClose}>
                                Cancel
                            </button>

                            <button
                                type="button"
                                onClick={async() => {
                                    let response = await linkGreenlightJobToCandidate(greenlightId);
                                    if (response) {
                                        TB.toast("Greenlight Job successfully linked", {type: 'success'});
                                        return onClose();
                                    }
                                }}
                                disabled={isDisabled || !greenlightId}
                                className="btn btn-primary">
                                Submit
                            </button>
                        </div>
                    </Form>
                </React.Fragment>

            </div>
        </SpinLoader>
    );
}

export default PositionCandidateGreenlightOptionsModal;